import type { RSUAsset } from "~/types/assets.types";
import type { Fiat } from "~/types/global.types";
import { calculateOwnership } from "~/utils/calculations.utils";
import { convertCurrency } from "../genericCalculations/convertCurrency";
import { calculateRSUShareProfitRate } from "./profit";
import { RsuTags } from "~/utils/tagEnums";


const CAPITAL_TAX_RATE = 0.25
const YESEF_TAX_RATE = 0.03
const EXTRA_YESEF_FOR_CAPITAL_GAINS_2025 = 0.02


export const calculateVestedPercent = (
  userProduct: RSUAsset,
) => {
    const rsuData =  userProduct?.rsuData || [];
    let total = 0;
    let vested = 0;
    rsuData.forEach((rsu) => {
        const totalAmount = rsu?.totalAmount || 0;
        const vestedAmount = rsu?.vestedAmount || 0;
        total += totalAmount;
        vested += vestedAmount;
    });
    return total === 0 ? 0 : vested / total;
};


export const calculateRSUTotalBalance = (
  userProduct: RSUAsset
) => {

  const rsus = userProduct?.rsuData || [];
  const { balanceInBaseCurrency: shareBalances } = calculateRSUBalance(userProduct)
  return rsus.reduce((sum, _, index) => {
    const shareBalance = shareBalances?.[index] || 0;

    return sum + shareBalance;
  }, 0);
};

export const calculateRSUBalance = (userProduct: RSUAsset) => {
  if (userProduct?.broker === RsuTags.OPTIONS) return calculateOptionsBalance(userProduct);
if (userProduct?.broker === RsuTags.ESPP) return calculateESPPBalance(userProduct);
  const rsuData = userProduct?.rsuData || [];
  const [balanceInBaseCurrency, balanceInShareCurrency]: [number[], number[]] = [[], []];
  rsuData.forEach((rsu) => {
    const grantDate = new Date(rsu?.grantDate || "");
    const currentDate = new Date();
    const twoYearsAgo = new Date(currentDate.getFullYear() - 2, currentDate.getMonth(), currentDate.getDate());
    const isOverTwoYears = !rsu?.grantDate || (grantDate < twoYearsAgo);
    const amount = userProduct.showVestedOnly ? rsu?.vestedAmount || 0 : rsu?.totalAmount || 0;
    const taxRate = userProduct.showAfterTax ? (Math.floor(userProduct?.taxRate || 0) / 100) + (userProduct.yesefTax ? YESEF_TAX_RATE : 0) : 0;
    let capitalGainesTax = userProduct.showAfterTax ? (CAPITAL_TAX_RATE + (userProduct.yesefTax ? YESEF_TAX_RATE + EXTRA_YESEF_FOR_CAPITAL_GAINS_2025 : 0)): 0;
    if (!userProduct.showSaleInTwoYears && !isOverTwoYears) {
      capitalGainesTax = taxRate;
    }
    const price = (userProduct?.stockPrice || 0);
    const grantPrice = Math.min(Number(rsu?.grantPrice || 0), price)
    const grantPriceInBaseCurrency = grantPrice * ((userProduct?.stockPriceInBaseCurrency || 0)/(userProduct?.stockPrice || 0));
    const tax = Math.max(((price - grantPrice) * amount * capitalGainesTax), 0) + (grantPrice * amount * taxRate);
    const priceInBaseCurrency = userProduct?.stockPriceInBaseCurrency || 0;
    const taxInBaseCurrency = Math.max(((priceInBaseCurrency - grantPriceInBaseCurrency) * amount * capitalGainesTax), 0) + (grantPriceInBaseCurrency * amount * taxRate);
    balanceInShareCurrency.push(amount * price - tax);
    balanceInBaseCurrency.push(amount * priceInBaseCurrency - taxInBaseCurrency);
  });
  return { balanceInBaseCurrency, balanceInShareCurrency };
};


export const calculateESPPBalance = (userProduct: RSUAsset) => {
    const rsuData = userProduct?.rsuData || [];
    const [balanceInBaseCurrency, balanceInShareCurrency]: [number[], number[]] = [[], []];
    rsuData.forEach((rsu) => {
        const grantDate = new Date(rsu?.grantDate || "");
        const currentDate = new Date();
        const twoYearsAgo = new Date(currentDate.getFullYear() - 2, currentDate.getMonth(), currentDate.getDate());
        const isOverTwoYears = !rsu?.grantDate || (grantDate < twoYearsAgo);
        const amount = rsu?.totalAmount || 0;
        const taxRate = userProduct.showAfterTax ? (Math.floor(userProduct?.taxRate || 0) / 100) + (userProduct.yesefTax ? YESEF_TAX_RATE : 0) : 0;
        let capitalGainesTax = userProduct.showAfterTax ? (CAPITAL_TAX_RATE + (userProduct.yesefTax ? YESEF_TAX_RATE + EXTRA_YESEF_FOR_CAPITAL_GAINS_2025 : 0)): 0;
        if (!userProduct.showSaleInTwoYears && !isOverTwoYears) {
            capitalGainesTax = taxRate;
        }
        const price = (userProduct?.stockPrice || 0);
        const priceInBaseCurrency = userProduct?.stockPriceInBaseCurrency || 0;
        const buyPrice = rsu?.buyPrice || 0;
        const stockPriceOnBuyDate = rsu?.stockPriceOnBuyDate || 0;
        const capitalGainesTexablePart = price - Math.min(price, stockPriceOnBuyDate);
        const laborTaxablePart = Math.max(0, stockPriceOnBuyDate - buyPrice);
        const tax = (Math.max(((capitalGainesTexablePart) * amount * capitalGainesTax), 0) + (laborTaxablePart * amount * taxRate));
        balanceInShareCurrency.push(amount * price - tax);
        balanceInBaseCurrency.push((amount * price - tax) * (priceInBaseCurrency / price || 0));
    })
    return { balanceInBaseCurrency, balanceInShareCurrency };
};


export const calculateOptionsBalance = (userProduct: RSUAsset) => {
    const rsuData = userProduct?.rsuData || [];
    const [balanceInBaseCurrency, balanceInShareCurrency]: [number[], number[]] = [[], []];
    rsuData.forEach((rsu) => {
        const grantDate = new Date(rsu?.grantDate || "");
        const currentDate = new Date();
        const twoYearsAgo = new Date(currentDate.getFullYear() - 2, currentDate.getMonth(), currentDate.getDate());
        const isOverTwoYears = !rsu?.grantDate || (grantDate < twoYearsAgo);
        const amount = userProduct.showVestedOnly ? rsu?.vestedAmount || 0 : rsu?.totalAmount || 0;
        const taxRate = userProduct.showAfterTax ? (Math.floor(userProduct?.taxRate || 0) / 100) + (userProduct.yesefTax ? YESEF_TAX_RATE : 0) : 0;
        let capitalGainesTax = userProduct.showAfterTax ? (CAPITAL_TAX_RATE + (userProduct.yesefTax ? YESEF_TAX_RATE + EXTRA_YESEF_FOR_CAPITAL_GAINS_2025 : 0)): 0;
        if (!userProduct.showSaleInTwoYears && !isOverTwoYears) {
            capitalGainesTax = taxRate;
        }
        const price = (userProduct?.stockPrice || 0);
        const priceInBaseCurrency = userProduct?.stockPriceInBaseCurrency || 0;
        const grantPrice = Math.min(Number(rsu?.grantPrice || 0), price); // Reduce grantPrice from total, but don't go under 0
        const finalPrice = price - grantPrice;
        const ipoDate = new Date(userProduct?.ipoPrice || "");
        const isWithin90Days = grantDate >= new Date(ipoDate.getTime() - (90 * 24 * 60 * 60 * 1000));
        let tax = 0;
        if (isWithin90Days) {
            const ipoPrice = userProduct?.ipoPrice || 0;
            const grantPrice = Math.min(Number(rsu?.grantPrice || 0), price);
            const taxableAmount = Math.max(ipoPrice - grantPrice, 0);
            const remainingAmount = Math.max(price - ipoPrice, 0);
            tax = (taxableAmount * amount * taxRate) + (remainingAmount * amount * capitalGainesTax);
        } else {
            tax = Math.max(((finalPrice) * amount * capitalGainesTax), 0);
        }
        balanceInShareCurrency.push(amount * finalPrice - tax);
        balanceInBaseCurrency.push((amount * finalPrice - tax) * (priceInBaseCurrency / price || 0));
    })
    return { balanceInBaseCurrency, balanceInShareCurrency };
};

export const calculateRSUCurrencyBalance = (
  userProduct: RSUAsset,
  baseCurrency: Fiat,
  currentCurrency: Fiat
) => {
  const rsuData = userProduct?.rsuData || [];
  const { balanceInBaseCurrency: rsuBalances } = calculateRSUBalance(userProduct);
  return rsuData.map((_, index) => {
    const rsuBalance = rsuBalances?.[index] || 0;
    return convertCurrency(rsuBalance, baseCurrency, currentCurrency);
  });
};

export const calculateRSUSumCurrencyBalance = (userProduct: RSUAsset) => {
  const rsuData = userProduct?.rsuData || [];
  const { balanceInBaseCurrency: currencyBalances } = calculateRSUBalance(userProduct);
  return rsuData.reduce((sum, _, index) => {
    const currencyBalance = currencyBalances?.[index] || 0;
    return sum + currencyBalance;
  }, 0);
};

export const convertRSUBalance = (
  userProduct: RSUAsset,
  currencies: {
    baseCurrency: Fiat;
    cashCurrency: Fiat;
  },
  withOnwership: boolean
) => {
  const ownership = userProduct?.ownership || 0;
  const balance = calculateRSUTotalBalance(userProduct);
  const balanceOwnership = withOnwership
    ? calculateOwnership(balance, ownership)
    : balance;
  return balanceOwnership;
};

export const calculateRSUsTotalBalanceOwnership = (
  userProduct: RSUAsset,
  cashCurrency: Fiat,
  BaseCurrency: Fiat
) => {
  const totalBalance = calculateRSUTotalBalance(
    userProduct
  );
  const ownership = userProduct?.ownership || 0;

  return calculateOwnership(totalBalance, ownership);
};
export const calculateRSUsProfitRate = (
  userProduct: RSUAsset,
  cashCurrency: Fiat,
  baseCurrency: Fiat
) => {
  const rsus = userProduct?.rsuData || [];
  const rsuShareProfitRates = calculateRSUShareProfitRate(userProduct);
  const partsInPortfolio = calculateOnlyRsusPortfolio(
    userProduct,
    cashCurrency,
    baseCurrency
  );

  return rsus.reduce((sum, _, index) => {
    const rsuShareProfitRate = rsuShareProfitRates?.[index] || 0;
    const partInPortfolio = partsInPortfolio?.[index] || 0;

    return sum + rsuShareProfitRate * partInPortfolio;
  }, 0);
};


export const calculateOnlyRsusPortfolio = (
    userProduct: RSUAsset,
    cashCurrency: Fiat,
    baseCurrency: Fiat
  ) => {
    const stocks = userProduct?.rsuData || [];
    const totalBalance = calculateRSUTotalBalance(
      userProduct
    );
    const { balanceInBaseCurrency: shareBalances } =
      calculateRSUBalance(userProduct);
  
    return stocks.map((_, index) => {
      const shareBalance = shareBalances?.[index] || 0;
  
      if (Number(totalBalance) === 0) return 0;
      return shareBalance / totalBalance;
    });
  };